// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#zombie-app-detection
import * as React from 'react';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ToggleRow } from './ToggleRow';
import type { AppLifecycleSettings, UpdateAppLifecycleSettings } from '@greenlight/shared';

export interface AppLifecycleSettingsProps {
  settings: AppLifecycleSettings | undefined;
  onUpdate: (patch: UpdateAppLifecycleSettings) => Promise<void>;
}

const asDays = (raw: string): number | null => {
  const n = Number(raw.trim());
  return Number.isInteger(n) && n >= 1 && n <= 3650 ? n : null;
};

/**
 * The org's one control over the unused-app lifecycle.
 *
 * The switch is the whole feature: off means no app is classified, no owner is
 * emailed, and nothing is ever paused. The day counts only exist in service of
 * it, so they are nested underneath and disabled when it is off rather than
 * presented as an independent setting — an org that has this switched off has no
 * use for a number that governs nothing.
 */
export function AppLifecycleSettings({
  settings,
  onUpdate,
}: AppLifecycleSettingsProps): React.JSX.Element {
  const enabled = settings?.enabled ?? false;
  const savedWarn = settings?.thresholds.inactive_days ?? 30;
  const savedPause = settings?.thresholds.auto_suspend_days ?? 90;

  const [warn, setWarn] = React.useState(String(savedWarn));
  const [pause, setPause] = React.useState(String(savedPause));
  const [saving, setSaving] = React.useState(false);

  // Re-seed the inputs whenever a save resolves, so the fields show what is
  // stored rather than a draft the server may have adjusted or refused.
  React.useEffect(() => {
    setWarn(String(savedWarn));
    setPause(String(savedPause));
  }, [savedWarn, savedPause]);

  const warnDays = asDays(warn);
  const pauseDays = asDays(pause);
  // Mirrors the server's rule so the refusal is visible before the round trip.
  const outOfOrder = warnDays !== null && pauseDays !== null && pauseDays <= warnDays;
  const dirty = warnDays !== savedWarn || pauseDays !== savedPause;
  const canSave =
    enabled && !saving && dirty && warnDays !== null && pauseDays !== null && !outOfOrder;

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onUpdate({ inactive_days: warnDays, auto_suspend_days: pauseDays });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard headerBar title="Unused apps">
      <ToggleRow
        label="Pause apps nobody is using"
        helper="Warn the owner when their app goes quiet, then pause it if it stays that way. Pausing is reversible and nothing is ever deleted."
        checked={enabled}
        onCheckedChange={(value) => {
          void onUpdate({ enabled: value });
        }}
      />
      <div className="space-y-3 px-5 py-3.5">
        <div className="flex flex-wrap items-end gap-4">
          <DayField
            label="Warn the owner after"
            value={warn}
            onChange={setWarn}
            disabled={!enabled}
            invalid={warnDays === null}
          />
          <DayField
            label="Pause the app after"
            value={pause}
            onChange={setPause}
            disabled={!enabled}
            invalid={pauseDays === null || outOfOrder}
          />
          <Button className="h-11 md:h-9" disabled={!canSave} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {outOfOrder && (
          <p className="text-sm text-destructive" role="alert">
            The pause day count must be larger than the one that warns the owner.
          </p>
        )}
      </div>
    </SectionCard>
  );
}

function DayField({
  label,
  value,
  onChange,
  disabled,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  invalid: boolean;
}): React.JSX.Element {
  const id = React.useId();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-sm text-foreground">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={1}
          max={3650}
          value={value}
          disabled={disabled}
          aria-invalid={invalid}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-24 md:h-9"
        />
        <span className="text-sm text-muted-foreground">days</span>
      </div>
    </div>
  );
}
